import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, MailIcon, PhoneIcon, MapPinIcon, CreditCardIcon, FileTextIcon, ActivityIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { Customer, PaymentMethod, CustomerTransaction } from '../../../shared/types/Customer';
export function CustomerDetail() {
  const {
    t
  } = useTranslation('customers');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [activeTab, setActiveTab] = useState<'invoices' | 'cards' | 'transactions'>('invoices');
  const customer: Customer = {
    id: id || '1',
    name: 'Acme Corporation',
    email: 'billing@acme.com',
    phone: '+66 12 345 6789',
    address: '123 Business Street',
    city: 'Kuala Lumpur',
    country: 'Malaysia',
    createdAt: '2023-06-15',
    totalSpent: 875000,
    invoiceCount: 12,
    status: 'active'
  };
  const paymentMethods: PaymentMethod[] = [{
    id: '1',
    type: 'visa',
    last4: '4242',
    expiryMonth: 12,
    expiryYear: 2025,
    cardholderName: 'John Doe',
    isDefault: true,
    addedAt: '2023-06-15'
  }, {
    id: '2',
    type: 'mastercard',
    last4: '5555',
    expiryMonth: 8,
    expiryYear: 2026,
    cardholderName: 'John Doe',
    isDefault: false,
    addedAt: '2023-09-20'
  }];
  const customerInvoices = [{
    id: '1',
    invoiceNumber: 'INV-2024-001',
    date: '2024-01-01',
    amount: 125000,
    status: 'paid' as const,
    dueDate: '2024-01-15'
  }, {
    id: '2',
    invoiceNumber: 'INV-2024-015',
    date: '2024-01-15',
    amount: 87500,
    status: 'sent' as const,
    dueDate: '2024-02-01'
  }, {
    id: '3',
    invoiceNumber: 'INV-2023-089',
    date: '2023-12-01',
    amount: 150000,
    status: 'paid' as const,
    dueDate: '2023-12-15'
  }];
  const transactions: CustomerTransaction[] = [{
    id: '1',
    date: '2024-01-10',
    description: 'Invoice Payment - INV-2024-001',
    amount: 125000,
    status: 'completed',
    paymentMethod: 'Visa •••• 4242',
    invoiceId: '1'
  }, {
    id: '2',
    date: '2023-12-10',
    description: 'Invoice Payment - INV-2023-089',
    amount: 150000,
    status: 'completed',
    paymentMethod: 'Visa •••• 4242',
    invoiceId: '3'
  }, {
    id: '3',
    date: '2023-11-15',
    description: 'Invoice Payment - INV-2023-078',
    amount: 95000,
    status: 'completed',
    paymentMethod: 'Mastercard •••• 5555'
  }, {
    id: '4',
    date: '2023-11-01',
    description: 'Refund - INV-2023-065',
    amount: -25000,
    status: 'refunded',
    paymentMethod: 'Visa •••• 4242'
  }];
  const history = [{
    id: 'h1',
    action: 'Invoice INV-2024-001 paid',
    date: '10 Jan 2024 at 14:32'
  }, {
    id: 'h2',
    action: 'Invoice INV-2024-015 sent',
    date: '15 Jan 2024 at 09:15'
  }, {
    id: 'h3',
    action: 'Payment method Visa •••• 4242 added',
    date: '15 Jun 2023 at 11:00'
  }, {
    id: 'h4',
    action: `${customer.name} was created`,
    date: '15 Jun 2023 at 10:30'
  }];
  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-50 text-green-700 border border-green-100',
      inactive: 'bg-gray-100 text-gray-600 border border-gray-200',
      draft: 'bg-gray-100 text-gray-800 border border-gray-200',
      sent: 'bg-blue-50 text-blue-700 border border-blue-100',
      paid: 'bg-green-50 text-green-700 border border-green-100',
      overdue: 'bg-red-50 text-red-700 border border-red-100',
      completed: 'bg-green-50 text-green-700 border border-green-100',
      pending: 'bg-yellow-50 text-yellow-700 border border-yellow-100',
      failed: 'bg-red-50 text-red-700 border border-red-100',
      refunded: 'bg-gray-100 text-gray-800 border border-gray-200'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`} data-id="element-5301" data-mp="5xqerw43wqko">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  return <div className="space-y-8 max-w-7xl mx-auto pb-12" data-id="element-5302" data-mp="3sc036581dylg">
      {/* Header */}
      <div className="flex items-center" data-id="element-5303" data-mp="3ecry4vp6zw9c">
        <button onClick={() => navigate('/customers')} className="flex items-center text-gray-900 hover:text-gray-600 transition-colors text-lg font-semibold" data-id="element-5304" data-mp="3eg640dx2enlj">
          <ArrowLeftIcon size={20} className="mr-2" data-id="element-5305" data-mp="34hqc48q7raaz" />
          {t('pages.CustomerDetail.all_customers')}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-5306" data-mp="12oio8awjbkdh">
        {/* Left Column - Main Details */}
        <div className="lg:col-span-2 space-y-6" data-id="element-5307" data-mp="17nvkfm9aq3sq">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-5308" data-mp="17mkfhj446q01">
            {/* Top Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-5309" data-mp="2n6bdvemr5aay">
              <div className="flex justify-between items-start" data-id="element-5310" data-mp="2n7w7n218sp7r">
                <div className="flex items-start gap-4" data-id="element-5311" data-mp="31vh6ukuzy4ae">
                  <div className="w-14 h-14 bg-[#FBD4D1] rounded-full flex items-center justify-center text-[#9F140F] font-semibold text-xl flex-shrink-0" data-id="element-5312" data-mp="31x0qmlfv2xgx">
                    {customer.name.charAt(0)}
                  </div>
                  <div data-id="element-5313" data-mp="36utu36ps5ddi">
                    <h1 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-5314" data-mp="36t91hmn104ia">
                      {customer.name}
                    </h1>
                    <div className="flex items-center text-sm" data-id="element-5315" data-mp="3krz6rpk01ahq">
                      <span className="text-gray-500 mr-2" data-id="element-5316" data-mp="3fwa59gof5jis">
                        {t('pages.CustomerDetail.status')}
                      </span>
                      {getStatusBadge(customer.status)}
                    </div>
                  </div>
                </div>
                <button onClick={() => navigate(`/customer-form?id=${customer.id}`)} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-5317" data-mp="3ljqkt8m7j1bd">
                  {t('pages.CustomerDetail.edit_customer')}
                </button>
              </div>
            </div>

            {/* Details Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-5318" data-mp="8bm8a209ao71">
              <div className="flex justify-between items-center mb-6" data-id="element-5319" data-mp="dbryreusi4nj">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-5320" data-mp="d9nngbuka3d8">
                  {t('pages.CustomerDetail.details')}
                </h3>
                <button onClick={() => navigate(`/customer-form?id=${customer.id}`)} className="text-sm font-medium text-[#9F140F] hover:text-[#8C6C00]" data-id="element-5321" data-mp="m30ro9y2srem">
                  {t('pages.CustomerDetail.edit')}
                </button>
              </div>
              <div className="space-y-4" data-id="element-5322" data-mp="166zmf5clz43m">
                <div className="grid grid-cols-3 gap-4" data-id="element-5323" data-mp="163ty3sbt068y">
                  <div className="text-sm text-gray-500" data-id="element-5324" data-mp="116bxqqbuqzck">
                    {t('pages.CustomerDetail.email')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-5325" data-mp="w9tujuw6792c">
                    {customer.email}
                  </div>
                </div>
                {customer.phone && <div className="grid grid-cols-3 gap-4" data-id="element-5326" data-mp="1oyimb3bc621f">
                    <div className="text-sm text-gray-500" data-id="element-5327" data-mp="1jyw9guzf6nt5">
                      {t('pages.CustomerDetail.phone')}
                    </div>
                    <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-5328" data-mp="1f4ie2skrcgoh">
                      {customer.phone}
                    </div>
                  </div>}
                <div className="grid grid-cols-3 gap-4" data-id="element-5329" data-mp="27pa1wmlx2oo9">
                  <div className="text-sm text-gray-500" data-id="element-5330" data-mp="22ve8lxgllpzf">
                    {t('pages.CustomerDetail.address')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-5331" data-mp="2mieltvos3i3g">
                    {customer.address}, {customer.city}, {customer.country}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4" data-id="element-5332" data-mp="2ck79xr9cjq8b">
                  <div className="text-sm text-gray-500" data-id="element-5333" data-mp="2hmfzseqxac67">
                    {t('pages.CustomerDetail.customer_since')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-5334" data-mp="3594q9otzzcyf">
                    {new Date(customer.createdAt).toLocaleDateString('en-GB')}
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-5335" data-mp="30a1ihwmmusmr">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6" data-id="element-5336" data-mp="1jxczzoa5o993">
                {t('pages.CustomerDetail.summary')}
              </h3>
              <div className="grid grid-cols-3 gap-6" data-id="element-5337" data-mp="1oucy9d0rk7pn">
                <div className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-5338" data-mp="6xsy1lr4hw9c">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-5339" data-mp="1yy7tyqtt5f7">
                    {t('pages.CustomerDetail.total_spent')}
                  </div>
                  <div className="text-lg font-semibold text-gray-900" data-id="element-5340" data-mp="fxodi2rpodf6">
                    {t('pages.CustomerDetail.usd')}{' '}
                    {customer.totalSpent.toLocaleString('en-US')}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-5341" data-mp="3ffp4ylqir2vn">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-5342" data-mp="3t62egsj26pn3">
                    {t('pages.CustomerDetail.total_invoices')}
                  </div>
                  <div className="text-lg font-semibold text-gray-900" data-id="element-5343" data-mp="3oe16vjwu5n3e">
                    {customer.invoiceCount}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-5344" data-mp="2woxwrni9piiu">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-5345" data-mp="2jx9lmfu0uj5x">
                    {t('pages.CustomerDetail.avg_invoice_value')}
                  </div>
                  <div className="text-lg font-semibold text-gray-900" data-id="element-5346" data-mp="2eyya80o2w0ll">
                    {t('pages.CustomerDetail.usd')}{' '}
                    {Math.round(customer.totalSpent / customer.invoiceCount).toLocaleString('en-US')}
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Methods Section */}
            <div className="p-8" data-id="element-5347" data-mp="1w6mallot7df5">
              <div className="flex justify-between items-center mb-6" data-id="element-5348" data-mp="1r8u9q266sgyt">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-5349" data-mp="1r942fxim9287">
                  {t('pages.CustomerDetail.payment_methods')}
                </h3>
                <button className="text-sm font-medium text-[#9F140F] hover:text-[#8C6C00] flex items-center gap-1.5" data-id="element-5350" data-mp="1i6ur55fix4tr">
                  <PlusIcon size={14} aria-hidden="true" data-id="element-5351" data-mp="1d9usvgox16d7" />
                  {t('pages.CustomerDetail.add_card')}
                </button>
              </div>
              <div className="space-y-3" data-id="element-5352" data-mp="3tuq31le41l8o">
                {paymentMethods.map(method => <div key={method.id} className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-5353" data-mp="2oygsx3gesr5">
                    <div className="flex items-center justify-between" data-id="element-5354" data-mp="6oh23tz6t5ui">
                      <div className="flex items-center gap-4" data-id="element-5355" data-mp="367i0hb82gqac">
                        <div className="w-10 h-10 rounded-full bg-[#FBD4D1] flex items-center justify-center" data-id="element-5356" data-mp="3b48fdv84xfu6">
                          <CreditCardIcon size={20} className="text-[#9F140F]" aria-hidden="true" data-id="element-5357" data-mp="3f5k6k56nq94h" />
                        </div>
                        <div data-id="element-5358" data-mp="2ndcpx4r5iwq3">
                          <div className="flex items-center gap-2" data-id="element-5359" data-mp="2rgsmy274uz3b">
                            <span className="text-sm font-semibold text-gray-900" data-id="element-5360" data-mp="2rexy4csqqmty">
                              {method.type.charAt(0).toUpperCase() + method.type.slice(1)}{' '}
                              •••• {method.last4}
                            </span>
                            {method.isDefault && <span className="px-2 py-0.5 bg-[#E2231A]/10 text-[#9F140F] text-xs font-medium rounded-md border border-[#E2231A]/20" data-id="element-5361" data-mp="3d0oadekzf58">
                                {t('pages.CustomerDetail.default')}
                              </span>}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5" data-id="element-5362" data-mp="3kujwlz6phrkl">
                            {t('pages.CustomerDetail.expires')}{' '}
                            {method.expiryMonth}/{method.expiryYear} ·{' '}
                            {method.cardholderName}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-5363" data-mp="13lqncwjbogmt">
                        {!method.isDefault && <button className="px-3 py-1.5 text-xs font-medium text-[#9F140F] hover:bg-[#FEE7E5] rounded-lg transition-colors" data-id="element-5364" data-mp="18l3bdklxjtqz">
                            {t('pages.CustomerDetail.set_as_default')}
                          </button>}
                        <button className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-5365" data-mp="1mbyk8wwfiauk">
                          <TrashIcon size={16} data-id="element-5366" data-mp="1rbb73hnavhx5" />
                        </button>
                      </div>
                    </div>
                  </div>)}
                {paymentMethods.length === 0 && <div className="text-center py-8 text-gray-500 text-sm" data-id="element-5367" data-mp="1hjx9xgitmu76">
                    {t('pages.CustomerDetail.no_payment_methods_stored')}
                  </div>}
              </div>
            </div>
          </div>

          {/* Tabs Section */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-5368" data-mp="mdhl59jmfyyc">
            <div className="flex space-x-6 px-8 border-b border-gray-100" role="tablist" aria-label="Customer details" data-id="element-5369" data-mp="hdv8b17pgkq2">
              <button role="tab" aria-selected={activeTab === 'invoices'} aria-controls="customer-panel-invoices" id="customer-tab-invoices" onClick={() => setActiveTab('invoices')} className={`pb-4 pt-5 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 ${activeTab === 'invoices' ? 'border-[#E2231A] text-[#9F140F]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-5370" data-mp="7fyq5pgcopig">
                <FileTextIcon size={16} aria-hidden="true" data-id="element-5371" data-mp="1nuqhs05o9sqp" />
                {t('pages.CustomerDetail.invoices')}
              </button>
              <button role="tab" aria-selected={activeTab === 'transactions'} aria-controls="customer-panel-transactions" id="customer-tab-transactions" onClick={() => setActiveTab('transactions')} className={`pb-4 pt-5 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 ${activeTab === 'transactions' ? 'border-[#E2231A] text-[#9F140F]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-5372" data-mp="1j2oz9whwv5s8">
                <ActivityIcon size={16} aria-hidden="true" data-id="element-5373" data-mp="2a95wsf9d3o8k" />
                {t('pages.CustomerDetail.transactions')}
              </button>
            </div>

            {/* Invoices Tab */}
            {activeTab === 'invoices' && <div className="overflow-x-auto" data-id="element-5374" data-mp="2o2n513wlleja">
                <table className="w-full text-left border-collapse" data-id="element-5375" data-mp="2o4hrimnipeph">
                  <thead data-id="element-5376" data-mp="2t2rn2yd5mhbm">
                    <tr className="border-b border-gray-100" data-id="element-5377" data-mp="2t3b329j8uvn0">
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5378" data-mp="2y1mgsvcntqai">
                        {t('pages.CustomerDetail.invoice')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5379" data-mp="23e45gnlo9si0">
                        {t('pages.CustomerDetail.date')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5380" data-mp="2db8qul7p1jxr">
                        {t('pages.CustomerDetail.due_date')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5381" data-mp="2m4c4oum93ysx">
                        {t('pages.CustomerDetail.amount')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-5382" data-mp="2w1grmwnx7yas">
                        {t('pages.CustomerDetail.status_2')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100" data-id="element-5383" data-mp="11q1zk97ftmrv">
                    {customerInvoices.map(invoice => <tr key={invoice.id} onClick={() => navigate(`/invoice-detail/${invoice.id}`)} className="hover:bg-gray-50 cursor-pointer transition-colors group" data-id="element-5384" data-mp="3iafdvidvtpfy">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 group-hover:text-[#9F140F] transition-colors" data-id="element-5385" data-mp="2ukbdrkzwk7tp">
                          {invoice.invoiceNumber}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500" data-id="element-5386" data-mp="2kngfn37jk9fy">
                          {new Date(invoice.date).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500" data-id="element-5387" data-mp="ivjtz0rntj7g">
                          {new Date(invoice.dueDate).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-5388" data-mp="36rmpp07q78z9">
                          {t('pages.CustomerDetail.usd')}{' '}
                          {invoice.amount.toLocaleString('en-US')}
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-5389" data-mp="2t1j7qlfvq1fm">
                          {getStatusBadge(invoice.status)}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}

            {/* Transactions Tab */}
            {activeTab === 'transactions' && <div className="overflow-x-auto" data-id="element-5390" data-mp="2f2a04a5qofn9">
                <table className="w-full text-left border-collapse" data-id="element-5391" data-mp="2a2x87b00gidt">
                  <thead data-id="element-5392" data-mp="2a4qayf7i6igj">
                    <tr className="border-b border-gray-100" data-id="element-5393" data-mp="da3oul8q5k9l">
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5394" data-mp="d8aspzvqmifu">
                        {t('pages.CustomerDetail.date')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5395" data-mp="fujlio9rpvzj">
                        {t('pages.CustomerDetail.description')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5396" data-mp="plehwlgqabyp">
                        {t('pages.CustomerDetail.payment_method')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5397" data-mp="3t364fkc3i3pi">
                        {t('pages.CustomerDetail.amount')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-5398" data-mp="6stsje08mozn">
                        {t('pages.CustomerDetail.status_2')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100" data-id="element-5399" data-mp="1macbi0cqs3ua">
                    {transactions.map(transaction => <tr key={transaction.id} className="hover:bg-gray-50 transition-colors" data-id="element-5400" data-mp="1n7t2s0chtkhk">
                        <td className="px-6 py-4 text-sm text-gray-500" data-id="element-5401" data-mp="1w0e8ofv0zwoz">
                          {new Date(transaction.date).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900" data-id="element-5402" data-mp="14bb572ayqqde">
                          {transaction.description}
                          {transaction.invoiceId && <button onClick={e => {
                      e.stopPropagation();
                      navigate(`/invoice-detail/${transaction.invoiceId}`);
                    }} className="ml-2 text-[#9F140F] hover:text-[#8C6C00] text-xs font-medium" data-id="element-5403" data-mp="1d9d9prtwo15f">
                              {t('pages.CustomerDetail.view_invoice')}
                            </button>}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500" data-id="element-5404" data-mp="89x8s5fy5exw">
                          {transaction.paymentMethod || '—'}
                        </td>
                        <td className="px-6 py-4 text-sm" data-id="element-5405" data-mp="3vghhhtfs7ril">
                          <span className={`font-medium ${transaction.amount < 0 ? 'text-red-600' : 'text-gray-900'}`} data-id="element-5406" data-mp="3vduymxnfxpl2">
                            {transaction.amount < 0 ? '-' : ''}
                            {t('pages.CustomerDetail.usd')}{' '}
                            {Math.abs(transaction.amount).toLocaleString('en-US')}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-5407" data-mp="397si4mfkjrew">
                          {getStatusBadge(transaction.status)}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>
        </div>

        {/* Right Column - History */}
        <div className="lg:col-span-1" data-id="element-5408" data-mp="349h8oct6rix7">
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-4" data-id="element-5409" data-mp="346cvwr1nneux">
            {t('pages.CustomerDetail.history')}
          </h3>
          <div className="space-y-6" data-id="element-5410" data-mp="qnym9zm9c7s">
            {history.map((event, index) => <div key={event.id} className="relative flex gap-4" data-id="element-5411" data-mp="3iydd7zw1adt2" data-mpi={index}>
                {index !== history.length - 1 && <div className="absolute left-[9px] top-6 bottom-[-24px] w-px bg-gray-200" data-id="element-5412" data-mp="3mxw2f7uw1b1p"></div>}
                <div className="relative z-10 flex-shrink-0 w-5 h-5 bg-gray-100 rounded flex items-center justify-center mt-0.5" data-id="element-5413" data-mp="27h5ark4y77rj">
                  <PlusIcon size={12} className="text-gray-600" data-id="element-5414" data-mp="240izuanmbs8n" />
                </div>
                <div data-id="element-5415" data-mp="242c63oubkag4">
                  <p className="text-sm font-medium text-gray-900" data-id="element-5416" data-mp="1z1xvbz1c2mee">
                    {event.action}
                  </p>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-5417" data-mp="2dxnmebldlppz">{event.date}</p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}